import { derived, get, writable, type Readable } from 'svelte/store';
import { app } from '$lib/stores/app/store';
import {
	buildFallbackChain,
	DEFAULT_LOCALE,
	DEFAULT_VIEWER_COUNTRY,
	toUnderscore,
	translate,
	type Locale,
	type Vars,
} from './locale';

export const activeLocale = writable<Locale>(DEFAULT_LOCALE);

type Bundle = Record<string, string>;
type Registry = Record<string, Record<Locale, Bundle>>;
type Loader = () => Promise<{ default: Bundle }>;
type Lookup = (key: string, vars?: Vars) => string;

const registry = writable<Registry>({});
const inFlight = new Map<string, Promise<void>>();

export type SharedL10nUnit = {
	readonly ns: string;
	preload: (locale: Locale, viewerCountry: string) => Promise<void>;
};

const localeFromPath = (path: string): Locale =>
	toUnderscore(path.split('/').pop()!.replace('.json', ''));

/**
 * Creates a store-backed localization unit over lazy locale bundles.
 * @param {Record<string, Loader>} loaders - Vite import.meta.glob locale loaders.
 * @param {string} ns - Registry namespace.
 * @param {Bundle} [staticDefaultBundle] - Synchronous en-US bootstrap bundle.
 * @param {SharedL10nUnit[]} [sharedUnits] - Additional units searched after this unit.
 * @returns {{ l10n: Readable<Lookup>; preload: SharedL10nUnit['preload']; ns: string }} Localization lookup store, preload function, and namespace.
 */
export function defineLazyL10n(
	loaders: Record<string, Loader>,
	ns: string,
	staticDefaultBundle?: Bundle,
	sharedUnits: SharedL10nUnit[] = []
): { l10n: Readable<Lookup>; preload: SharedL10nUnit['preload']; ns: string } {
	const byLocale: Record<Locale, Loader> = {};
	for (const [path, loader] of Object.entries(loaders)) byLocale[localeFromPath(path)] = loader;

	if (staticDefaultBundle) {
		registry.update((current) => {
			if (current[ns]?.[DEFAULT_LOCALE]) return current;
			return {
				...current,
				[ns]: {
					...current[ns],
					[DEFAULT_LOCALE]: staticDefaultBundle,
				},
			};
		});
	}

	/**
	 * Loads one authored locale at most once.
	 * @param {Locale} locale - Locale to load.
	 * @returns {Promise<void>} Promise settled after the import attempt.
	 */
	async function loadOne(locale: Locale): Promise<void> {
		if (get(registry)[ns]?.[locale] || !byLocale[locale]) return;

		const cacheKey = `${ns}:${locale}`;
		let promise = inFlight.get(cacheKey);
		if (!promise) {
			promise = byLocale[locale]()
				.then((module) => {
					registry.update((current) => ({
						...current,
						[ns]: {
							...current[ns],
							[locale]: module.default,
						},
					}));
				})
				.catch(() => {});
			inFlight.set(cacheKey, promise);
		}
		await promise;
	}

	/**
	 * Loads all available fallback and shared-unit bundles.
	 * @param {Locale} locale - Active locale.
	 * @param {string} viewerCountry - Two-letter viewer country code.
	 * @returns {Promise<void>} Promise settled after all import attempts.
	 */
	async function preload(locale: Locale, viewerCountry: string): Promise<void> {
		await Promise.all([
			...buildFallbackChain(locale, viewerCountry).map(loadOne),
			...sharedUnits.map((unit) => unit.preload(locale, viewerCountry)),
		]);
	}

	const l10n = derived([activeLocale, registry, app], ([$activeLocale, $registry, $app]) => {
		// Meta is absent until CloudFront values land (and in tests that stub the app store),
		// so the geo rung falls back rather than breaking every subscriber.
		const viewerCountry = $app?.meta?.viewerCountry ?? DEFAULT_VIEWER_COUNTRY;
		const chain = buildFallbackChain($activeLocale, viewerCountry);
		const sources = [$registry[ns], ...sharedUnits.map((unit) => $registry[unit.ns])];
		return (key: string, vars?: Vars): string => translate(sources, chain, key, vars);
	});

	return { l10n, preload, ns };
}
